import { AddRouteToRoot, CheckExistingFilters, RenderItems, UpdatePaginationButtons } from "../search/search-helpers";
import {
    SearchInputEventData,
    SearchFilterEventEventData,
    searchFilterEventInit,
    searchFilterEventUpdate,
    searchEvent,
    searchInputEventInit,
    searchInputEventUpdate,
    searchTagEventUpdate,
    SearchTagEventData,
    searchFilterResetEvent,
} from "../search/search-events";
import SearchResultModel, { SearchOptions } from "../search/search-model";

export default class SearchAgent<T> {
    listingContainer: HTMLDivElement;

    //Listing
    SearchResultsCount: HTMLHeadingElement;
    searchResultsElement: HTMLDivElement;
    searchResultItemTemplate: HTMLDivElement;
    searchResultsGrid: HTMLDivElement;
    noResultsContainer: HTMLDivElement;
    processing: boolean;
    itemList: HTMLElement[] = [];

    //Buttons
    showMoreBtn: HTMLDivElement;
    showMoreWrapper: HTMLElement;
    filterButton: HTMLButtonElement;

    //Tag count
    allResultsCount: HTMLElement;

    //Search
    filters: SearchFilterEventEventData[] = [];
    searchOptions: SearchOptions;
    controllerURL: string;
    searchTags: string = "";
    searchMode: "news" | "general";
    lastSearch: string = "";

    //Pagination
    paginationContainer: HTMLDivElement;
    paginationPrev: HTMLElement;
    paginationNext: HTMLElement;

    page: number = 1;
    itemsToShow: number = 10;
    maximumPages: number = 1;
    columns: number;
    isLastPage: boolean;
    totalNumberOfResults: number = 0;
    isFiltered: boolean = false;

    constructor(elementId: string, searchOptions: string) {
        this.listingContainer = document.getElementById(elementId) as HTMLDivElement;
        this.SetElements();
        this.UpdateResults = this.UpdateResults.bind(this);
        this.GenerateListing = this.GenerateListing.bind(this);
        if (searchOptions) {
            this.searchOptions = JSON.parse(searchOptions);
            this.controllerURL = AddRouteToRoot(this.searchOptions.controllerURL);
        }
        this.AddEventListeners();
    }

    SetElements() {
        //Listing
        this.searchResultsElement = this.listingContainer.querySelector('.search-results') as HTMLDivElement;
        this.searchResultsGrid = this.searchResultsElement.querySelector('.grid__container') as HTMLDivElement;
        this.noResultsContainer = this.searchResultsElement.querySelector('.no-results');

        this.paginationPrev = this.searchResultsElement.querySelector(".pagination__prev button") as HTMLElement;
        this.paginationNext = this.searchResultsElement.querySelector(".pagination__next button") as HTMLElement;
        this.paginationContainer = document.querySelector(".search-pagination") as HTMLDivElement;

        //Buttons
        this.allResultsCount = document.querySelector('.search-tag-widget__container .tag__count');
    }
    InstantiateGridLayout() {
        if (this.searchResultsGrid) {
            this.ResetSearchResultsGrid();
        }
        RenderItems(this.searchResultsGrid, this.itemList, this.itemsToShow);
    }

    Init() {
        // Initialise empty page with search
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('keyword')) {
            setTimeout(() => {
                const initEventData: SearchInputEventData = {
                    searchInput: ""
                }
                searchEvent.emit(searchInputEventInit, initEventData);
            }, 1);
        }

        //Page Load
        searchEvent.on(searchInputEventInit, (initEventData: SearchInputEventData) => {
            this.lastSearch = initEventData?.searchInput;
            this.GenerateListing();
        });

        searchEvent.on(searchInputEventUpdate, (updateEventData: SearchInputEventData) => {
            this.lastSearch = updateEventData.searchInput;
            this.page = 1;
            this.GenerateListing();
        });

        searchEvent.on(searchFilterEventInit, (filterEventData: SearchFilterEventEventData) => {
            CheckExistingFilters(this.filters, filterEventData);
        });
        searchEvent.on(searchFilterEventUpdate, (filterEventData: SearchFilterEventEventData) => {
            CheckExistingFilters(this.filters, filterEventData);
            this.page = 1;
            this.GenerateListing();
            if (this.filterButton) {
                this.UpdateFilterNumber();
            }
        });
        searchEvent.on(searchFilterResetEvent, () => {
            this.searchTags = '';
            this.isFiltered = true;
            this.filters = [];
            this.GenerateListing();
            this.UpdateFilterNumber();
        });
        searchEvent.on(searchTagEventUpdate, (filterEventData: SearchTagEventData) => {
            this.isFiltered = true;
            this.searchTags = filterEventData.tag;
            this.GenerateListing();
        });
    }

    AddEventListeners() {
        this.paginationPrev.addEventListener("click", () => {
            const firstItem = this.searchResultsGrid.querySelectorAll('.grid-item .search-result-card')[0] as HTMLAnchorElement;

            this.page--;
            this.Search(this.lastSearch);
            window.scrollTo({
                top: 90,
                behavior: 'smooth'
            });
            if (firstItem) firstItem.focus()
        });

        this.paginationNext.addEventListener("click", () => {
            const firstItem = this.searchResultsGrid.querySelectorAll('.grid-item .search-result-card')[0] as HTMLAnchorElement;

            this.page++;
            this.Search(this.lastSearch);
            window.scrollTo({
                top: 90,
                behavior: 'smooth'
            });
            if (firstItem) firstItem.focus()
        });

        const paginationButtons = this.paginationContainer.querySelector('.pagination__buttons');
        paginationButtons.addEventListener("click", (event) => {
            const target = event.target as HTMLElement;
            if (target?.tagName === "BUTTON" && target?.hasAttribute("data-page")) {
                event.preventDefault();
                const newPage = parseInt(target?.getAttribute("data-page"));
                const firstItem = this.searchResultsGrid.querySelectorAll('.grid-item .search-result-card')[0] as HTMLAnchorElement;

                this.page = newPage;
                this.Search(this.lastSearch);
                window.scrollTo({
                    top: 90,
                    behavior: 'smooth'
                });
                if (firstItem) firstItem.focus()
            }
        });

        const filterButton = this.listingContainer.querySelector('.search-results__filter a') as HTMLAnchorElement;
        if (filterButton) {
            filterButton.setAttribute('data-bs-toggle', 'offcanvas')
            filterButton.setAttribute('role', 'button')
        }
    }

    async Search(query: string) {
        if (query != null) {
            let filters = JSON.stringify(this.filters);

            await fetch(`${this.controllerURL}?query=${query}&page=${this.page}&itemsPerPage=${this.itemsToShow}&searchIndexes=${this.searchOptions.searchIndexes}&searchTags=${this.searchTags}&filters=${filters}&isSuggestionMode=false&isNewsSearch=${this.searchMode == "news"}`)
                .then(async response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    const data = await response.json()
                    try {
                        this.UpdateResults(data, this.isFiltered);
                    } catch (error) {
                        console.error("Error parsing JSON:", error);
                    }
                })
                .catch(err => console.error("Error while fetching search API", err))

        }
    }

    //Show all results on page load
    GenerateListing() {
        this.processing = true;
        this.Search(this.lastSearch);
    }
    UpdateButtons(index: number) {
        UpdatePaginationButtons(index, this.isLastPage, this.maximumPages, this.paginationContainer);
    }

    UpdateFilterNumber() {
        const filterNumberWrapper = document.querySelector('.filter-button #filter-button__total');
        const filterNumberLabel = filterNumberWrapper.querySelector('.label');
        const filterNumber = filterNumberWrapper.querySelector('.filter-num');
        if (this.filters.length > 0 && this.filters.some(fil => fil.searchValue != '')) {
            let filterCount = 0;
            this.filters.forEach(fil => {
                if (fil.searchValue != '') {
                    filterCount += fil.searchValue.split(',').length
                }
            })
            filterNumber.textContent = `(${filterCount})`
            filterNumberLabel.textContent = `Current filters applied: `
        } else {
            filterNumber.textContent = ''
            filterNumberLabel.textContent = 'No filters currently applied'
        }
    }
    UpdateResults(data: SearchResultModel<T>, isFiltered: boolean) {
        this.searchResultItemTemplate = this.listingContainer.querySelector(".item--template") as HTMLDivElement;
        if (isFiltered) this.searchResultsGrid.innerHTML = '';
        if (this.itemList.length > 0) this.itemList = [];
        if (this.searchResultsGrid != null) {
            const jsonObj: SearchResultModel<T> = data;
            this.totalNumberOfResults = jsonObj.TotalNumberOfResults || 0;
            this.maximumPages = Math.ceil(this.totalNumberOfResults / this.itemsToShow);
            if (jsonObj.Items.length > 0) {
                for (let i: number = 0; i < jsonObj.Items?.length; i++) {
                    this.CreateItems(jsonObj.Items[i], i);
                }
                if (this.itemList.length > 0) {
                    this.InstantiateGridLayout();
                }
            }

            if (this.allResultsCount)
                this.allResultsCount.textContent = data.TotalNumberOfResultsBeforeFilter.toString();
        }
        if (this.searchResultsGrid != null) {
            this.UpdateButtons(this.page);
            this.UpdateResultIndicator()
        }
        if (this.totalNumberOfResults == 0) {
            const noresultClone = this.noResultsContainer.cloneNode(true) as HTMLElement;
            noresultClone.classList.remove('d-none');
            this.ResetSearchResultsGrid();
            this.searchResultsGrid.appendChild(noresultClone);
        }
        if (this.isLastPage) {
            const lastPageItem = this.totalNumberOfResults - (this.itemsToShow * (this.page - 1));
            const itemList = document.querySelectorAll('.grid-item');
            itemList.forEach((item: HTMLElement, index) => {
                if (index >= lastPageItem) {
                    item.style.display = 'none';
                } else {
                    item.style.display = '';
                }
            })
        }
        this.processing = false;
        this.paginationContainer.style.visibility = 'visible';
    }
    UpdateResultIndicator() {
        const indicatorWrapper = this.listingContainer.querySelector('.results-indicator');
        indicatorWrapper.innerHTML = `
            <span class="results-num" id="search-results-label">${this.totalNumberOfResults} results found</span>  
        `;

        if (this.maximumPages != 0) {
            indicatorWrapper.innerHTML += `<span aria-hidden="true">|</span><span class="page-num">Page ${this.page} of ${this.maximumPages}`;
        }
    }
    ResetSearchResultsGrid() {
        this.searchResultsGrid.innerHTML = ""
    }
    CreateItems(data: any, index: number) {
        console.log(data, index);
    }
}